import BrokerTreesService from "../../Endpoints/BrokerTrees";
import brokersService from "../../Endpoints/BrokersIBs";
import TradingGroupsService from "../../Endpoints/TradingGroups";
import EntitiesService from "../../Endpoints/SettingEntities";
import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  Button,
  Switch,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Chip,
  Divider,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIosNewSmallIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  buildTableRowsPerPageOptions,
  TABLE_INITIAL_ROWS_PER_PAGE,
  TABLE_HEADER_FONT_SIZE,
} from "../Table/constants.js";

const steps = ["Tree set-up", "Group selection", "Splits"];

/* =========================
   Stepper styles
========================= */
const RedConnector = styled(StepConnector)(() => ({
  top: 13,
  "& .MuiStepConnector-line": {
    borderTopWidth: 3,
    borderColor: "#E5E7EB",
    borderRadius: 999,
  },
  "&.Mui-active .MuiStepConnector-line": {
    borderColor: "#DC2626",
  },
  "&.Mui-completed .MuiStepConnector-line": {
    borderColor: "#DC2626",
  },
}));

function RedStepIcon(props) {
  const { active, completed, icon } = props;
  const isOn = active || completed;

  return (
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        fontSize: 12,
        fontWeight: 900,
        color: isOn ? "white" : "#111827",
        background: isOn ? "#DC2626" : "#E5E7EB",
      }}
    >
      {icon}
    </Box>
  );
}

/* =========================
   Helpers
========================= */
const clampPercent = (v) => {
  const n = Number(String(v).replace("%", "").trim());
  if (Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(100, n));
};

const mapLanguageFromApiToUi = (value) => {
  const map = {
    ENGLISH: "ENG",
    SPANISH: "SPA",
    PORTUGUESE: "POR",
    ENG: "ENG",
    SPA: "SPA",
    POR: "POR",
  };

  return map[value] ?? value ?? "";
};

const mapLanguageFromUiToApi = (value) => {
  const map = {
    ENG: "ENGLISH",
    SPA: "SPANISH",
    POR: "PORTUGUESE",
  };

  return map[value] ?? value ?? "";
};

const resolveTreeStatusBoolean = (rawStatus, fallbackStatus) => {
  if (typeof rawStatus === "boolean") return rawStatus;
  if (typeof rawStatus === "string") {
    const normalized = rawStatus.trim().toUpperCase();
    if (normalized === "ENABLED" || normalized === "ACTIVE" || normalized === "TRUE") {
      return true;
    }
    if (normalized === "DISABLED" || normalized === "INACTIVE" || normalized === "FALSE") {
      return false;
    }
  }

  if (typeof fallbackStatus === "boolean") return fallbackStatus;
  if (typeof fallbackStatus === "string") {
    const normalizedFallback = fallbackStatus.trim().toUpperCase();
    if (
      normalizedFallback === "ENABLED" ||
      normalizedFallback === "ACTIVE" ||
      normalizedFallback === "TRUE"
    ) {
      return true;
    }
    if (
      normalizedFallback === "DISABLED" ||
      normalizedFallback === "INACTIVE" ||
      normalizedFallback === "FALSE"
    ) {
      return false;
    }
  }

  return true;
};

const toInt = (value) => {
  const numberValue = Number(String(value ?? "").trim());
  return Number.isFinite(numberValue) ? numberValue : null;
};

const normalizeBrokerStatus = (value) => {
  const normalized = String(value ?? "").trim().toUpperCase();
  if (normalized === "ENABLED") return "ACTIVE";
  if (normalized === "DISABLED") return "INACTIVE";
  return normalized;
};

const normalizeUserType = (value) =>
  String(value ?? "").trim().toUpperCase();

const isBrokerUser = (value) => normalizeUserType(value) === "BROKER";

const isIbUser = (value) => {
  const normalized = normalizeUserType(value);

  return (
    normalized === "IB" ||
    normalized === "INTRODUCING_BROKER" ||
    normalized === "INTRODUCER" ||
    normalized === "INTRODUCINGBROKER"
  );
};

const formatIbOptionLabel = (option) => {
  if (!option) return "";
  const name = String(option?.name ?? "").trim();
  const id = String(option?.id ?? "").trim();
  if (name && id) return `${name} (${id})`;
  return name || id;
};

function flattenTradingGroups(apiPayload) {
  const groups = Array.isArray(apiPayload?.groups) ? apiPayload.groups : [];

  return groups.map((group, index) => ({
    id: group?.id ?? index + 1,
    group: String(group?.group ?? "").trim(),
    server: String(group?.server ?? "").trim(),
    currency: String(group?.currency ?? "").trim(),
    leverage: group?.leverage ?? "",
    mcso: String(group?.mcso ?? "").trim(),
    groupSplit: Array.isArray(group?.group_split) ? group.group_split : [],
  }));
}

const GROUP_TABLE_COLUMNS = [
  {
    field: "select",
    label: "",
    minWidth: 52,
    width: 52,
    align: "center",
    resizable: false,
  },
  { field: "group", label: "Group", minWidth: 170, width: 190 },
  { field: "server", label: "Server", minWidth: 130, width: 150 },
  { field: "currency", label: "Currency", minWidth: 110, width: 120 },
  { field: "leverage", label: "Leverage", minWidth: 110, width: 120 },
  { field: "mcso", label: "MCSO", minWidth: 110, width: 120 },
];

const buildInitialColumnWidths = (columns) =>
  columns.reduce((widths, column) => {
    widths[column.field] = column.width;
    return widths;
  }, {});

const initialGroupTableColumnWidths = buildInitialColumnWidths(GROUP_TABLE_COLUMNS);

function useResizableColumns(columns, initialWidths) {
  const [columnWidths, setColumnWidths] = useState(initialWidths);

  const tableMinWidth = useMemo(() => {
    return columns.reduce(
      (total, column) => total + (columnWidths[column.field] || column.width),
      0
    );
  }, [columns, columnWidths]);

  const columnCellSx = useMemo(() => {
    return columns.reduce((styles, column) => {
      const width = columnWidths[column.field] || column.width;

      styles[column.field] = {
        width,
        minWidth: column.minWidth,
        maxWidth: width,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      };

      return styles;
    }, {});
  }, [columns, columnWidths]);

  const handleColumnResizeStart = (event, column) => {
    if (column.resizable === false) return;

    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startWidth = columnWidths[column.field] || column.width;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const handlePointerMove = (moveEvent) => {
      const nextWidth = Math.max(
        column.minWidth,
        startWidth + moveEvent.clientX - startX
      );

      setColumnWidths((prev) => ({
        ...prev,
        [column.field]: nextWidth,
      }));
    };

    const handlePointerUp = () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
  };

  const handleColumnResizeKeyDown = (event, column) => {
    if (column.resizable === false) return;

    const direction =
      event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;

    if (!direction) return;

    event.preventDefault();
    event.stopPropagation();

    setColumnWidths((prev) => {
      const currentWidth = prev[column.field] || column.width;

      return {
        ...prev,
        [column.field]: Math.max(column.minWidth, currentWidth + direction * 16),
      };
    });
  };

  return {
    columnWidths,
    tableMinWidth,
    columnCellSx,
    handleColumnResizeStart,
    handleColumnResizeKeyDown,
  };
}

const normalizeTreeToForm = (tree) => {
  const rawTree = tree?.rawTree ?? tree ?? {};
  const owners = Array.isArray(rawTree?.owners)
    ? rawTree.owners
    : Array.isArray(tree?.owners)
    ? tree.owners
    : [];
  const splits = Array.isArray(rawTree?.splits)
    ? rawTree.splits
    : Array.isArray(tree?.splitSettings)
    ? tree.splitSettings
    : [];
  // "IB Owners" (multi-select). Reads from `ib_owners` on the raw tree
  // payload, the same shape used by the create-tree endpoint:
  // "ib_owners": [{ "id": 1015, "name": "...", "email": "..." }, ...]
  const ibOwnersRaw = Array.isArray(rawTree?.ib_owners)
    ? rawTree.ib_owners
    : Array.isArray(tree?.ibOwners)
    ? tree.ibOwners
    : [];
  const owner =
    rawTree?.owner ??
    owners?.[0]?.user ??
    (tree?.ownerId || tree?.ownerType
      ? {
          id: tree?.ownerId ?? null,
          name: tree?.ownerType ?? "",
        }
      : null);
  const masterIb =
    rawTree?.master_ib ??
    (tree?.masterIbId || tree?.masterIbName || tree?.ownerName
      ? {
          id: tree?.masterIbId ?? null,
          name: tree?.masterIbName ?? tree?.ownerName ?? "",
        }
      : null);
  const ownerId = owner?.id ? String(owner.id) : "";
  const normalizedOwnersFromApi = owners
    .map((o) => ({
      userId:
        o?.user?.id || o?.userId ? String(o?.user?.id ?? o?.userId) : "",
      percentage: clampPercent(o?.percentage ?? 0),
    }))
    .filter((o) => String(o.userId).trim().length > 0);

  const hasSubOwners =
    normalizedOwnersFromApi.length > 0 &&
    normalizedOwnersFromApi.some((o) => String(o.userId) !== ownerId);

  const subOwnersOnly = hasSubOwners
    ? normalizedOwnersFromApi.filter((o) => String(o.userId) !== ownerId)
    : [];

  const brokerPercentageFromSubOwners = Math.max(
    0,
    100 -
      subOwnersOnly.reduce((acc, o) => acc + clampPercent(o?.percentage ?? 0), 0)
  );

  const ownerRowForForm = ownerId
    ? {
        userId: ownerId,
        percentage: hasSubOwners
          ? brokerPercentageFromSubOwners
          : normalizedOwnersFromApi.find((o) => String(o.userId) === ownerId)?.percentage ?? 100,
      }
    : null;

  const normalizedOwnersForForm = hasSubOwners
    ? [ownerRowForForm, ...subOwnersOnly].filter(Boolean)
    : ownerRowForForm
    ? [ownerRowForForm]
    : [];

  return {
    id: rawTree?.tree_id ?? tree?.id ?? null,
    ownerId: owner?.id ?? null,
    ownerName: owner?.name ?? "",
    treeName: rawTree?.tree_name ?? tree?.name ?? "",
    entity: String(rawTree?.entity ?? tree?.entity ?? ""),
    language: mapLanguageFromApiToUi(rawTree?.language ?? tree?.language),
    masterIbId: masterIb?.id ? String(masterIb.id) : "",
    masterIbName: masterIb?.name ?? "",
    // Keep both id and name so the Autocomplete can display previously
    // selected IB owners even if they are no longer in the enabled IB list.
    ibOwners: ibOwnersRaw
      .map((o) => ({
        id: o?.id ? String(o.id) : "",
        name: o?.name ?? "",
      }))
      .filter((o) => o.id),
    ownershipMode: hasSubOwners ? "SUB" : "FULL",
    group: rawTree?.group ?? tree?.group ?? "",
    server: rawTree?.server ?? tree?.server ?? "MT4 LTD Demo",
    status: resolveTreeStatusBoolean(
      rawTree?.status,
      tree?.statusValue ?? tree?.status ?? rawTree?.account_status ?? tree?.account_status
    ),
    owners: normalizedOwnersForForm,
    splits: splits.map((s) => ({
      ibId: s?.user?.id || s?.ibId ? String(s?.user?.id ?? s?.ibId) : "",
      ibName: s?.user?.name ?? s?.ibName ?? "",
      sharesPercent: clampPercent(s?.percentage ?? s?.shares ?? 0),
      showLink: Boolean(s?.referral_link_flag ?? s?.accessIbLink),
      seeClients: Boolean(s?.client_view_flag ?? s?.clientView),
      seeFullPercent: Boolean(s?.show_full_flag ?? s?.showFull),
    })),
  };
};

export default function EditTreePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const rowFromState = location?.state?.row ?? null;

  const [activeStep, setActiveStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loadingTree, setLoadingTree] = useState(false);
  const [error, setError] = useState("");
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [updatedTreeId, setUpdatedTreeId] = useState(null);
  const [brokersLoading, setBrokersLoading] = useState(false);
  const [brokersError, setBrokersError] = useState("");
  const [brokerOptions, setBrokerOptions] = useState([]);
  const [ibOptions, setIbOptions] = useState([]);
  const [tradingGroupsLoading, setTradingGroupsLoading] = useState(false);
  const [tradingGroupsError, setTradingGroupsError] = useState("");
  const [tradingGroups, setTradingGroups] = useState([]);
  const [groupSearch, setGroupSearch] = useState("");
  const [serverFilter, setServerFilter] = useState("");
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [groupPage, setGroupPage] = useState(1);
  const GROUP_ROWS_OPTIONS = useMemo(
    () => buildTableRowsPerPageOptions([5, 7, 10, 25, 50, 100]),
    []
  );
  const [groupRowsPerPage, setGroupRowsPerPage] = useState(
    TABLE_INITIAL_ROWS_PER_PAGE
  );
  const [entityOptions, setEntityOptions] = useState([]);
  const [brokerShareDraft, setBrokerShareDraft] = useState("100");
  const [stepOneHalfOpen, setStepOneHalfOpen] = useState(false);
  const [subOwnerDraft, setSubOwnerDraft] = useState({
    userId: "",
    percentage: "",
  });

  const [form, setForm] = useState({
    id: null,
    ownerId: null,
    ownerName: "",
    treeName: "",
    entity: "",
    language: "ENG",
    masterIbId: "",
    masterIbName: "",
    ibOwners: [],
    ownershipMode: "FULL",
    group: "",
    server: "MT4 LTD Demo",
    status: true,
    owners: [],
    splits: [],
  });

  useEffect(() => {
    const loadTree = async () => {
      const id = Number(params.id) || null;

      if (rowFromState) {
        setForm(normalizeTreeToForm(rowFromState));
        return;
      }

      if (!id) {
        setError("Invalid tree id.");
        return;
      }

      try {
        setLoadingTree(true);
        setError("");

        const res = await BrokerTreesService.getBrokerTrees();
        if (!res) {
          setError("Failed to load tree data.");
          return;
        }
        const payload = res?.data?.data ?? res?.data ?? null;
        const trees = Array.isArray(payload?.trees)
          ? payload.trees
          : Array.isArray(payload)
          ? payload
          : payload
          ? [payload]
          : [];

        const found = trees.find(
          (tree) => Number(tree?.tree_id ?? tree?.id) === Number(id)
        );

        if (!found) {
          setError(`Tree with id ${id} was not found.`);
          return;
        }

        setForm(normalizeTreeToForm(found));
      } catch (err) {
        setError(err?.message || "Failed to load tree data.");
      } finally {
        setLoadingTree(false);
      }
    };

    loadTree();
  }, [rowFromState, params.id]);

  useEffect(() => {
    const firstOwnerPercentage = clampPercent(form?.owners?.[0]?.percentage ?? 100);
    setBrokerShareDraft(String(firstOwnerPercentage));
  }, [form?.id, form?.owners]);

  useEffect(() => {
    let alive = true;

    const loadEnabledUsers = async () => {
      setBrokersLoading(true);
      setBrokersError("");

      try {
        const response = await brokersService.BrokersIBs();
        if (!response) {
          throw new Error("We couldn't load enabled brokers and IBs.");
        }

        const users = Array.isArray(response?.data?.data?.users)
          ? response.data.data.users
          : [];

        const enabledUsers = users
          .map((user) => ({
            id: user?.id ?? "",
            name: user?.name ?? "",
            email: user?.email ?? "",
            userType: normalizeUserType(
              user?.user_type ?? user?.userType ?? user?.type ?? ""
            ),
            status: normalizeBrokerStatus(
              user?.account_status ?? user?.status ?? user?.status_backend ?? ""
            ),
          }))
          .filter((user) => toInt(user.id))
          .filter((user) => user.status === "ACTIVE")
          .sort((a, b) =>
            String(a.name || "").localeCompare(String(b.name || ""), undefined, {
              sensitivity: "base",
            })
          );

        if (!alive) return;

        setBrokerOptions(enabledUsers.filter((user) => isBrokerUser(user.userType)));
        setIbOptions(enabledUsers.filter((user) => isIbUser(user.userType)));
      } catch (loadError) {
        if (!alive) return;

        setBrokerOptions([]);
        setIbOptions([]);
        setBrokersError("We couldn't load enabled brokers and IBs.");
      } finally {
        if (alive) setBrokersLoading(false);
      }
    };

    loadEnabledUsers();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;

    const loadEntities = async () => {
      try {
        const response = await EntitiesService.getEntities();
        const payload = response?.data?.data ?? response?.data ?? {};
        const entities = Array.isArray(payload?.entities) ? payload.entities : [];
        const names = Array.from(
          new Set(
            entities
              .map((entity) => String(entity?.name ?? "").trim())
              .filter(Boolean)
          )
        ).sort((left, right) =>
          left.localeCompare(right, undefined, { sensitivity: "base" })
        );

        if (!alive) return;
        setEntityOptions(names);
      } catch (loadError) {
        if (!alive) return;
        setEntityOptions([]);
      }
    };

    loadEntities();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;

    const loadTradingGroups = async () => {
      setTradingGroupsLoading(true);
      setTradingGroupsError("");

      try {
        const res = await TradingGroupsService.getTradingGroups();
        if (!res) {
          if (!alive) return;
          setTradingGroups([]);
          setTradingGroupsError("We couldn't load trading groups.");
          return;
        }

        const payload = res?.data?.data ?? res?.data ?? null;
        const rows = flattenTradingGroups(payload).filter((row) => row.group);

        if (!alive) return;
        setTradingGroups(rows);
      } catch (loadError) {
        if (!alive) return;
        setTradingGroups([]);
        setTradingGroupsError("We couldn't load trading groups.");
      } finally {
        if (alive) setTradingGroupsLoading(false);
      }
    };

    loadTradingGroups();

    return () => {
      alive = false;
    };
  }, []);

  const cardSx = {
    maxWidth: 920,
    mx: "auto",
    background: "white",
    border: "1px solid #E5E7EB",
    borderRadius: 3,
    p: { xs: 2, md: 3 },
  };

  const innerPanelSx = {
    border: "1px solid #EEF2F7",
    borderRadius: 3,
    p: 2,
    background: "white",
    boxShadow: "0 12px 30px rgba(17, 24, 39, 0.06)",
  };

  const greenCheckboxSx = {
    color: "#111827",
    "&.Mui-checked": { color: "#15803D" },
    "&.MuiCheckbox-indeterminate": { color: "#15803D" },
  };

  const addBtnSx = (enabled) => ({
    textTransform: "none",
    fontWeight: 800,
    borderRadius: 2,
    background: enabled ? "#DC2626" : "#E5E7EB",
    color: enabled ? "white" : "#111827",
    boxShadow: enabled ? "0 8px 16px rgba(220, 38, 38, 0.18)" : "none",
    "&:hover": { background: enabled ? "#B91C1C" : "#D1D5DB" },
  });

  const blackBtnSx = {
    textTransform: "none",
    fontWeight: 800,
    borderRadius: 2,
    background: "#FFFFFF",
    color: "#111827",
    borderColor: "#111827",
    boxShadow: "none",
    "&:hover": {
      background: "#F9FAFB",
      borderColor: "#030712",
      color: "#030712",
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      background: "#FFFFFF",
      color: "#9CA3AF",
      borderColor: "#E5E7EB",
    },
  };

  const totalShares = useMemo(
    () => form.splits.reduce((acc, s) => acc + clampPercent(s.sharesPercent), 0),
    [form.splits]
  );

  const remainingShares = Math.max(0, 100 - totalShares);
  const overflow = totalShares > 100;

  const setField = (key, value) => {
    setError("");
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const brokerOwnerDisplayName = useMemo(() => {
    const selectedBroker = brokerOptions.find(
      (broker) => String(broker.id) === String(form.ownerId)
    );

    return selectedBroker?.name || form.ownerName || "";
  }, [brokerOptions, form.ownerId, form.ownerName]);

  const selectedMasterIbOption = useMemo(() => {
    const selected = ibOptions.find(
      (ib) => String(ib.id) === String(form.masterIbId)
    );

    if (selected) return selected;
    if (!String(form.masterIbId).trim()) return null;

    return {
      id: String(form.masterIbId),
      name: form.masterIbName || `IB ${form.masterIbId}`,
    };
  }, [ibOptions, form.masterIbId, form.masterIbName]);

  const masterIbAutocompleteOptions = useMemo(() => {
    if (!selectedMasterIbOption) return ibOptions;

    const exists = ibOptions.some(
      (ib) => String(ib.id) === String(selectedMasterIbOption.id)
    );

    return exists ? ibOptions : [selectedMasterIbOption, ...ibOptions];
  }, [ibOptions, selectedMasterIbOption]);

  // "IB Owners" multi-select: resolve each stored { id, name } against the
  // enabled IB list (so labels stay accurate/up to date), falling back to
  // the name captured when the tree was loaded if the IB is no longer in
  // the enabled list.
  const selectedIbOwnerOptions = useMemo(() => {
    return (form.ibOwners || []).map((owner) => {
      const matched = ibOptions.find(
        (ib) => String(ib.id) === String(owner.id)
      );
      return matched || { id: owner.id, name: owner.name || `IB ${owner.id}` };
    });
  }, [form.ibOwners, ibOptions]);

  const ibOwnersAutocompleteOptions = useMemo(() => {
    const missingSelected = selectedIbOwnerOptions.filter(
      (selected) => !ibOptions.some((ib) => String(ib.id) === String(selected.id))
    );

    return [...missingSelected, ...ibOptions];
  }, [ibOptions, selectedIbOwnerOptions]);

  const handleIbOwnersChange = (selectedOptions) => {
    setError("");
    setForm((prev) => ({
      ...prev,
      ibOwners: (selectedOptions || []).map((option) => ({
        id: String(option.id),
        name: option?.name ?? "",
      })),
    }));
  };

  const selectedSubOwnerDraftOption = useMemo(
    () =>
      brokerOptions.find(
        (broker) => String(broker.id) === String(subOwnerDraft.userId)
      ) ?? null,
    [brokerOptions, subOwnerDraft.userId]
  );

  const subOwners = useMemo(() => {
    if (!Array.isArray(form.owners) || form.owners.length <= 1) return [];
    return form.owners.slice(1);
  }, [form.owners]);

  const subOwnersTotalPercent = useMemo(
    () => subOwners.reduce((acc, owner) => acc + clampPercent(owner?.percentage ?? 0), 0),
    [subOwners]
  );
  const subOwnerDraftPercent = clampPercent(subOwnerDraft.percentage);
  const effectiveSubOwnersTotalPercent = useMemo(() => {
    if (subOwnerDraftPercent <= 0) return subOwnersTotalPercent;

    const draftUserId = String(subOwnerDraft.userId ?? "").trim();
    if (!draftUserId) {
      return Math.max(0, subOwnersTotalPercent + subOwnerDraftPercent);
    }

    const existing = subOwners.find(
      (owner) => String(owner?.userId ?? "") === draftUserId
    );
    const existingPercent = existing ? clampPercent(existing.percentage) : 0;
    return Math.max(0, subOwnersTotalPercent - existingPercent + subOwnerDraftPercent);
  }, [subOwnersTotalPercent, subOwners, subOwnerDraft.userId, subOwnerDraftPercent]);
  const ownershipTotalPercent =
    clampPercent(brokerShareDraft) + subOwnersTotalPercent;
  const ownershipExceedsLimit = ownershipTotalPercent > 100;
  const brokerShareRemaining = Math.max(0, 100 - effectiveSubOwnersTotalPercent);

  const subOwnersWithMeta = useMemo(
    () =>
      subOwners.map((owner) => {
        const broker = brokerOptions.find(
          (option) => String(option.id) === String(owner?.userId ?? "")
        );
        return {
          userId: String(owner?.userId ?? ""),
          percentage: clampPercent(owner?.percentage ?? 0),
          name: broker?.name || `Broker ${owner?.userId ?? ""}`,
        };
      }),
    [subOwners, brokerOptions]
  );

  const canAddSubOwner =
    String(subOwnerDraft.userId).trim().length > 0 &&
    clampPercent(subOwnerDraft.percentage) > 0 &&
    effectiveSubOwnersTotalPercent <= 100;

  useEffect(() => {
    if (form.ownershipMode !== "SUB") return;

    setBrokerShareDraft(String(brokerShareRemaining));
    setForm((prev) => {
      const nextOwners = Array.isArray(prev.owners) ? [...prev.owners] : [];
      const ownerId = prev.ownerId ? String(prev.ownerId) : "";
      const ownerRow = {
        userId: ownerId,
        percentage: brokerShareRemaining,
      };

      if (nextOwners.length > 0) {
        nextOwners[0] = { ...nextOwners[0], ...ownerRow };
      } else {
        nextOwners.push(ownerRow);
      }

      return { ...prev, owners: nextOwners };
    });
  }, [form.ownershipMode, brokerShareRemaining]);

  const {
    columnWidths: groupColumnWidths,
    tableMinWidth: groupTableMinWidth,
    columnCellSx: groupColumnCellSx,
    handleColumnResizeStart: handleGroupColumnResizeStart,
    handleColumnResizeKeyDown: handleGroupColumnResizeKeyDown,
  } = useResizableColumns(GROUP_TABLE_COLUMNS, initialGroupTableColumnWidths);

  const filteredGroups = useMemo(() => {
    const search = groupSearch.toLowerCase().trim();
    return tradingGroups.filter((group) => {
      const okSearch =
        !search ||
        [
          group.group,
          group.server,
          group.currency,
          group.leverage,
          group.mcso,
        ]
          .map((value) => String(value ?? "").toLowerCase())
          .some((value) => value.includes(search));
      const okServer = !serverFilter || group.server === serverFilter;
      return okSearch && okServer;
    });
  }, [tradingGroups, groupSearch, serverFilter]);

  const paginatedGroups = useMemo(() => {
    const start = (groupPage - 1) * groupRowsPerPage;
    return filteredGroups.slice(start, start + groupRowsPerPage);
  }, [filteredGroups, groupPage, groupRowsPerPage]);

  const groupPageCount = Math.max(
    1,
    Math.ceil(filteredGroups.length / groupRowsPerPage)
  );

  const groupPageItems = useMemo(() => {
    const last = groupPageCount;
    const current = groupPage;

    if (last <= 4) return Array.from({ length: last }, (_, index) => index + 1);
    if (current <= 2) return [1, 2, 3, 4, "..."];
    if (current >= last - 1) return ["...", last - 3, last - 2, last - 1, last];

    return ["...", current - 1, current, current + 1, "..."];
  }, [groupPageCount, groupPage]);

  const serverOptions = useMemo(() => {
    const normalizedMap = new Map();

    tradingGroups.forEach((group) => {
      const rawServer = String(group?.server ?? "").trim();
      if (!rawServer) return;

      const normalizedKey = rawServer.toLowerCase();
      if (!normalizedMap.has(normalizedKey)) {
        normalizedMap.set(normalizedKey, rawServer);
      }
    });

    return Array.from(normalizedMap.values()).sort((left, right) =>
      left.localeCompare(right, undefined, { sensitivity: "base" })
    );
  }, [tradingGroups]);

  const selectedGroup = useMemo(
    () => tradingGroups.find((group) => selectedGroupIds.includes(group.id)) ?? null,
    [tradingGroups, selectedGroupIds]
  );
  const currentRowGroup = String(form.group ?? "").trim();
  const currentRowServer = String(form.server ?? "").trim();

  useEffect(() => {
    setGroupPage((prev) => Math.min(prev, groupPageCount));
  }, [groupPageCount]);

  useEffect(() => {
    if (!tradingGroups.length || selectedGroupIds.length > 0) return;

    const normalizedGroup = String(form.group ?? "").trim().toLowerCase();
    const normalizedServer = String(form.server ?? "").trim().toLowerCase();
    if (!normalizedGroup) return;

    const matched = tradingGroups.find(
      (group) =>
        String(group.group).trim().toLowerCase() === normalizedGroup &&
        (!normalizedServer ||
          String(group.server).trim().toLowerCase() === normalizedServer)
    );

    if (matched) {
      setSelectedGroupIds([matched.id]);
    }
  }, [tradingGroups, selectedGroupIds.length, form.group, form.server]);

  const handleMasterIbChange = (selectedOption) => {
    const selectedId = selectedOption?.id ? String(selectedOption.id) : "";

    setError("");
    setForm((prev) => ({
      ...prev,
      masterIbId: selectedId,
      masterIbName: selectedOption?.name ?? "",
    }));
  };

  const addSubOwner = () => {
    if (!canAddSubOwner) return;

    setForm((prev) => {
      const nextOwners = Array.isArray(prev.owners) ? [...prev.owners] : [];
      const nextSubOwner = {
        userId: String(subOwnerDraft.userId),
        percentage: clampPercent(subOwnerDraft.percentage),
      };

      const existingIndex = nextOwners.findIndex(
        (owner, index) =>
          index > 0 && String(owner?.userId ?? "") === String(nextSubOwner.userId)
      );

      if (existingIndex >= 0) {
        nextOwners[existingIndex] = nextSubOwner;
      } else {
        nextOwners.push(nextSubOwner);
      }

      return { ...prev, owners: nextOwners };
    });

    setSubOwnerDraft({ userId: "", percentage: "" });
  };

  const removeSubOwner = (userId) => {
    setForm((prev) => {
      const nextOwners = Array.isArray(prev.owners) ? [...prev.owners] : [];
      if (nextOwners.length <= 1) return prev;

      const baseOwner = nextOwners[0];
      const filtered = nextOwners
        .slice(1)
        .filter((owner) => String(owner?.userId ?? "") !== String(userId));

      return { ...prev, owners: [baseOwner, ...filtered] };
    });
  };

  const setSplitField = (index, key, value) => {
    setError("");
    setForm((prev) => {
      const next = [...prev.splits];
      next[index] = { ...next[index], [key]: value };
      return { ...prev, splits: next };
    });
  };

  const addSplitRow = () => {
    setError("");
    setForm((prev) => ({
      ...prev,
      splits: [
        ...prev.splits,
        {
          ibId: "",
          ibName: "",
          sharesPercent: 0,
          showLink: false,
          seeClients: false,
          seeFullPercent: false,
        },
      ],
    }));
  };

  const removeSplitRow = (index) => {
    setError("");
    setForm((prev) => ({
      ...prev,
      splits: prev.splits.filter((_, i) => i !== index),
    }));
  };

  const step1Valid =
    String(form.treeName).trim().length > 0 &&
    String(form.entity).trim().length > 0 &&
    String(form.language).trim().length > 0;

  const step2Valid = selectedGroupIds.length > 0;

  const step3Valid =
    !overflow &&
    totalShares === 100 &&
    form.splits.length > 0 &&
    form.splits.every((s) => String(s.ibId).trim().length > 0);

  const canContinue =
    (activeStep === 0 && step1Valid) ||
    (activeStep === 1 && step2Valid) ||
    (activeStep === 2 && step3Valid);

  const toggleGroup = (id) => {
    const isSelected = selectedGroupIds.includes(id);
    if (isSelected) {
      setSelectedGroupIds([]);
      setField("group", "");
      return;
    }

    const nextSelected = tradingGroups.find((group) => group.id === id);
    setSelectedGroupIds([id]);
    setField("group", nextSelected?.group || "");
    setField("server", nextSelected?.server || "MT4 LTD Demo");
  };

  const groupArrowBtnSx = (disabled) => ({
    minWidth: 30,
    height: 30,
    borderRadius: "10px",
    backgroundColor: "#FFFFFF",
    border: "1px solid #E5E7EB",
    color: disabled ? "#9CA3AF" : "#374151",
    boxShadow: "none",
    padding: 0,
    "&:hover": {
      backgroundColor: "#F9FAFB",
      borderColor: "#D1D5DB",
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      backgroundColor: "#FFFFFF",
      borderColor: "#E5E7EB",
      color: "#9CA3AF",
      opacity: 1,
    },
  });

  const handleBack = () => {
    if (saving) return;

    if (activeStep === 0 && stepOneHalfOpen) {
      setStepOneHalfOpen(false);
      return;
    }

    if (activeStep === 0) navigate(-1);
    else setActiveStep((s) => s - 1);
  };

  const handleNext = () => {
    if (saving) return;

    if (activeStep === 0 && !stepOneHalfOpen) {
      setStepOneHalfOpen(true);
      return;
    }

    if (activeStep === 0 && stepOneHalfOpen && ownershipExceedsLimit) {
      return;
    }

    if (activeStep === 1 && selectedGroup) {
      setField("group", selectedGroup.group || "");
      setField("server", selectedGroup.server || "MT4 LTD Demo");
    }

    setActiveStep((s) => Math.min(2, s + 1));
  };

  const handleSave = async () => {
    console.log("CLICK SAVE", {
      step3Valid,
      formId: form.id,
      saving,
      form,
    });

    if (!form.id || saving) {
      console.log("STOP SAVE", {
        reason: !form.id ? "Missing form.id" : "Already saving",
      });
      return;
    }

    const ownersPayload =
      Array.isArray(form.owners) && form.owners.length > 0
        ? form.owners
            .filter((o) => String(o.userId).trim().length > 0)
            .map((o) => ({
              user: {
                id: Number(o.userId),
              },
              percentage: clampPercent(o.percentage),
            }))
        : form.ownershipMode === "FULL" && form.ownerId
        ? [
            {
              user: {
                id: Number(form.ownerId),
              },
              percentage: 100,
            },
          ]
        : [];

    // "IB Owners" multi-select -> [{ id: <ibId> }, ...], same shape used by
    // the create-tree endpoint.
    const ibOwnersPayload = (form.ibOwners || [])
      .map((owner) => ({ id: toInt(owner.id) }))
      .filter((owner) => !!owner.id);

    const payload = {
      tree_id: Number(form.id),
      tree_name: form.treeName,
      master_ib: form.masterIbId
        ? {
            id: Number(form.masterIbId),
          }
        : null,
      ib_owners: ibOwnersPayload,
      group: form.group,
      server: form.server || "MT4 LTD Demo",
      entity: String(form.entity).trim(),
      language: mapLanguageFromUiToApi(form.language),
      status: Boolean(form.status),
      splits: form.splits.map((s) => ({
        user: {
          id: Number(s.ibId),
        },
        percentage: clampPercent(s.sharesPercent),
        client_view_flag: Boolean(s.seeClients),
        referral_link_flag: Boolean(s.showLink),
        show_full_flag: Boolean(s.seeFullPercent),
      })),
      owners: ownersPayload,
    };

    try {
      setSaving(true);
      setError("");

      console.log("UPDATE TREE PAYLOAD:", JSON.stringify(payload, null, 2));

      const response = await BrokerTreesService.updateBrokerTree(payload);
      if (!response) {
        setError("Failed to update tree.");
        return;
      }

      console.log("UPDATE TREE SUCCESS:", response);

      setUpdatedTreeId(form.id);
      setOpenSuccessModal(true);
    } catch (err) {
      console.error("UPDATE TREE ERROR:", err);
      setError(err?.message || "Failed to update tree.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        px: { xs: 2, md: 6 },
        py: 3,
        background:
          "radial-gradient(900px 500px at 90% 60%, rgba(59,130,246,.18), transparent 60%), radial-gradient(700px 400px at 10% 70%, rgba(236,72,153,.16), transparent 55%), #F9FAFB",
      }}
    >
      <Box
        sx={{
          maxWidth: 920,
          mx: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Button
          startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 16 }} />}
          onClick={() => !saving && navigate(-1)}
          sx={{ textTransform: "none", color: "#111827" }}
        >
          Go back
        </Button>

        <IconButton onClick={() => !saving && navigate(-1)} sx={{ color: "#111827" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ maxWidth: 920, mx: "auto", mb: 2 }}>
        <Typography sx={{ fontSize: 28, fontWeight: 900, color: "#111827" }}>
          Edit a Tree
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: 13, color: "#6B7280" }}>
          Fine-tune your tree by updating its set-up, groups, and rebate splits.
        </Typography>
      </Box>

      <Box sx={{ maxWidth: 920, mx: "auto", mb: 2 }}>
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          connector={<RedConnector />}
          sx={{
            "& .MuiStepLabel-label": { fontSize: 12, color: "#6B7280" },
            "& .MuiStepLabel-label.Mui-active": { color: "#111827", fontWeight: 700 },
            "& .MuiStepLabel-label.Mui-completed": {
              color: "#111827",
              fontWeight: 700,
            },
            ...(activeStep === 0 && {
              "& .MuiStep-root:nth-of-type(2) .MuiStepConnector-line": {
                position: "relative",
              },
              "& .MuiStep-root:nth-of-type(2) .MuiStepConnector-line::after": {
                content: '""',
                position: "absolute",
                left: 0,
                top: "50%",
                transform: "translateY(-50%)",
                width: "50%",
                height: 3,
                backgroundColor: "#DC2626",
                borderRadius: 999,
                pointerEvents: "none",
              },
            }),
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel StepIconComponent={RedStepIcon}>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Box sx={cardSx}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          <Typography sx={{ fontWeight: 900, color: "#111827" }}>
            {activeStep === 0 && stepOneHalfOpen
              ? "Step 1.5: Tree set-up"
              : `Step ${activeStep + 1}: ${steps[activeStep]}`}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
                Account status:
              </Typography>
              <Switch
                checked={Boolean(form.status)}
                onChange={(e) => setField("status", e.target.checked)}
              />
            </Box>

            <Chip
              label={`Tree ID: ${form.id ?? "—"}`}
              sx={{
                borderRadius: 999,
                background: "#EEF2F7",
                color: "#111827",
                fontWeight: 800,
              }}
            />
          </Box>
        </Box>

        {loadingTree && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <CircularProgress size={18} />
            <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
              Loading tree data...
            </Typography>
          </Box>
        )}

        {!!error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {activeStep === 0 && (
          <Box sx={{ ...innerPanelSx, borderRadius: 4 }}>
            {!stepOneHalfOpen && (
              <>
            <Typography sx={{ fontSize: 12, color: "#6B7280", mb: 2 }}>
              Update the tree’s name, entity, language, and ownership configuration.
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)",
                },
                gap: 2,
              }}
            >
              <TextField
                size="small"
                label="Broker Owner"
                value={brokerOwnerDisplayName}
                disabled
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    background: "#FAFAFA",
                  },
                }}
              />

              <TextField
                size="small"
                label="Tree name"
                value={form.treeName}
                onChange={(e) => setField("treeName", e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />

              <FormControl size="small">
                <InputLabel>Entity</InputLabel>
                <Select
                  label="Entity"
                  value={form.entity}
                  onChange={(e) => setField("entity", e.target.value)}
                >
                  {entityOptions.map((entityName) => (
                    <MenuItem key={entityName} value={entityName}>
                      {entityName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small">
                <InputLabel>Language</InputLabel>
                <Select
                  label="Language"
                  value={form.language}
                  onChange={(e) => setField("language", e.target.value)}
                >
                  <MenuItem value="ENG">ENG</MenuItem>
                  <MenuItem value="SPA">SPA</MenuItem>
                  <MenuItem value="POR">POR</MenuItem>
                </Select>
              </FormControl>

              <Autocomplete
                fullWidth
                options={masterIbAutocompleteOptions}
                value={selectedMasterIbOption}
                loading={brokersLoading}
                noOptionsText={
                  brokersLoading ? "Loading IBs..." : "No enabled IBs found."
                }
                getOptionLabel={(option) => {
                  if (!option) return "";
                  const name = String(option?.name ?? "").trim();
                  const id = String(option?.id ?? "").trim();

                  if (name && id) return `${name} (${id})`;
                  return name || id;
                }}
                isOptionEqualToValue={(option, value) =>
                  String(option?.id) === String(value?.id)
                }
                onChange={(_, option) => handleMasterIbChange(option)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Master IB (optional)"
                    placeholder="Search IB..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              {/*
                IB Owners: same enabled-IB dataset as Master IB, but multiple
                selection, pre-filled with the tree's existing `ib_owners`.
                Uses MUI's Autocomplete (multiple + checkboxes), matching the
                same pattern already used on the create-tree screen so it
                shares the exact same positioning/anchoring behaviour as
                every other Autocomplete field in this form.
              */}
              <Autocomplete
                fullWidth
                multiple
                disableCloseOnSelect
                limitTags={2}
                options={ibOwnersAutocompleteOptions}
                value={selectedIbOwnerOptions}
                loading={brokersLoading}
                noOptionsText={
                  brokersLoading ? "Loading IBs..." : "No enabled IBs found."
                }
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) =>
                  String(option?.id) === String(value?.id)
                }
                onChange={(_, selectedOptions) =>
                  handleIbOwnersChange(selectedOptions)
                }
                renderOption={(props, option, { selected }) => {
                  const { key, ...optionProps } = props;
                  return (
                    <Box component="li" key={key} {...optionProps}>
                      <Checkbox
                        checked={selected}
                        sx={{ ...greenCheckboxSx, mr: 1, p: 0.5 }}
                      />
                      {formatIbOptionLabel(option)}
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="IB Owners (optional)"
                    placeholder={
                      selectedIbOwnerOptions.length ? "" : "Search IB..."
                    }
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            {brokersError ? (
              <Typography
                sx={{ mt: 1.5, color: "#B91C1C", fontWeight: 800, fontSize: 12 }}
              >
                {brokersError}
              </Typography>
            ) : null}

            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: 12, color: "#6B7280", mb: 1 }}>
                Ownership distribution
              </Typography>

              <RadioGroup
                row
                value={form.ownershipMode}
                onChange={(e) => setField("ownershipMode", e.target.value)}
                sx={{ gap: 2 }}
              >
                <Box
                  sx={{
                    flex: 1,
                    border: "1px solid #E5E7EB",
                    borderRadius: 2,
                    px: 2,
                    py: 1.2,
                    background: form.ownershipMode === "FULL" ? "#EEF6FF" : "white",
                  }}
                >
                  <FormControlLabel
                    value="FULL"
                    control={<Radio />}
                    label="Full ownership of main Broker/IB"
                  />
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    border: "1px solid #E5E7EB",
                    borderRadius: 2,
                    px: 2,
                    py: 1.2,
                    background: form.ownershipMode === "SUB" ? "#EEF6FF" : "white",
                  }}
                >
                  <FormControlLabel
                    value="SUB"
                    control={<Radio />}
                    label="Sub-owners"
                  />
                </Box>
              </RadioGroup>
            </Box>
              </>
            )}

            {stepOneHalfOpen && (
              <Box sx={{ mt: 2, border: "1px solid #EEF2F7", borderRadius: 3, p: 2, background: "#F9FAFB" }}>
                <Typography sx={{ fontSize: 12, color: "#6B7280", mb: 2 }}>
                  Define the ownership share. The total sum of shares, including the broker owner and
                  the sub-owners, must equal 100%.
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 260px" },
                    gap: 2,
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      border: "1px solid #EEF2F7",
                      borderRadius: 3,
                      px: 2,
                      py: 1.4,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.2,
                      background: "white",
                    }}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        background: "#111827",
                        color: "white",
                      }}
                    >
                      <PeopleAltOutlinedIcon fontSize="small" />
                    </Box>

                    <Typography sx={{ fontSize: 13, color: "#374151", fontWeight: 700 }}>
                      Broker owner:&nbsp;
                      <span style={{ color: "#111827" }}>{brokerOwnerDisplayName || "—"}</span>
                    </Typography>

                    <Box sx={{ flex: 1 }} />

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <TextField
                        size="small"
                        value={brokerShareDraft}
                        onChange={(e) => {
                          const value = e.target.value;
                          setBrokerShareDraft(value);
                          setForm((prev) => {
                            const nextOwners = Array.isArray(prev.owners) ? [...prev.owners] : [];
                            const parsed = Number(String(value).replace("%", "").trim());
                            const safeValue = Number.isFinite(parsed)
                              ? clampPercent(parsed)
                              : 0;

                            if (nextOwners.length > 0) {
                              nextOwners[0] = {
                                ...nextOwners[0],
                                percentage: safeValue,
                              };
                            } else {
                              nextOwners.push({
                                userId: prev.ownerId ? String(prev.ownerId) : "",
                                percentage: safeValue,
                              });
                            }

                            return { ...prev, owners: nextOwners };
                          });
                        }}
                        onBlur={() => {
                          if (String(brokerShareDraft).trim()) return;
                          setBrokerShareDraft("100");
                          setForm((prev) => {
                            const nextOwners = Array.isArray(prev.owners) ? [...prev.owners] : [];
                            if (nextOwners.length > 0) {
                              nextOwners[0] = { ...nextOwners[0], percentage: 100 };
                            } else {
                              nextOwners.push({
                                userId: prev.ownerId ? String(prev.ownerId) : "",
                                percentage: 100,
                              });
                            }
                            return { ...prev, owners: nextOwners };
                          });
                        }}
                        placeholder="--% of shares"
                        inputProps={{ inputMode: "numeric" }}
                        sx={{
                          width: 150,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            background: "white",
                            fontWeight: 700,
                          },
                        }}
                      />
                      <IconButton
                        size="small"
                        sx={{ border: "1px solid #E5E7EB", borderRadius: 2, color: "#111827" }}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      border: "1px solid #EEF2F7",
                      borderRadius: 3,
                      px: 2,
                      py: 1.4,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      background: "white",
                    }}
                  >
                    <Typography sx={{ fontSize: 12, color: "#6B7280", fontWeight: 800 }}>
                      Total share count:
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: "#111827", fontWeight: 900 }}>
                      {Math.max(0, ownershipTotalPercent)}%
                    </Typography>
                  </Box>
                </Box>

                <Typography sx={{ fontSize: 12, color: "#6B7280", textAlign: "center" }}>
                  Remaining to assign:{" "}
                  <b style={{ color: "#111827" }}>
                    {Math.max(
                      0,
                      100 - clampPercent(brokerShareDraft) - subOwnersTotalPercent
                    )}
                    %
                  </b>
                </Typography>

                {ownershipExceedsLimit && (
                  <Typography
                    sx={{
                      mt: 1,
                      fontSize: 12,
                      color: "#B91C1C",
                      fontWeight: 800,
                      textAlign: "center",
                    }}
                  >
                    The total share percentage cannot exceed 100%.
                  </Typography>
                )}

                {form.ownershipMode === "SUB" && (
                  <>
                    <Typography
                      sx={{ fontSize: 16, color: "#6B7280", textAlign: "center", mt: 2, mb: 1.5 }}
                    >
                      Remember that the total sum of shares, including yours and the shareholders&apos;,
                      must equal 100%.
                    </Typography>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: 2,
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Autocomplete
                        fullWidth
                        size="small"
                        options={brokerOptions}
                        value={selectedSubOwnerDraftOption}
                        loading={brokersLoading}
                        noOptionsText={brokersError || "No enabled broker owners found."}
                        getOptionLabel={(option) => option?.name ?? ""}
                        isOptionEqualToValue={(option, value) =>
                          String(option?.id ?? "") === String(value?.id ?? "")
                        }
                        onChange={(_, selectedOption) =>
                          setSubOwnerDraft((prev) => ({
                            ...prev,
                            userId: selectedOption?.id ? String(selectedOption.id) : "",
                          }))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="List of broker owners"
                            placeholder="Search broker owner..."
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                  </InputAdornment>
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                            }}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                          />
                        )}
                      />

                      <TextField
                        size="small"
                        label="Shares %"
                        placeholder="--%"
                        value={subOwnerDraft.percentage}
                        onChange={(e) =>
                          setSubOwnerDraft((prev) => ({
                            ...prev,
                            percentage: e.target.value,
                          }))
                        }
                        inputProps={{ inputMode: "numeric" }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                      />
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                      <Button
                        variant="contained"
                        onClick={addSubOwner}
                        disabled={!canAddSubOwner}
                        sx={addBtnSx(canAddSubOwner)}
                      >
                        + Add broker
                      </Button>
                    </Box>

                    {subOwnersWithMeta.length > 0 && (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2, mb: 1 }}>
                        {subOwnersWithMeta.map((owner) => (
                          <Box
                            key={owner.userId}
                            sx={{
                              border: "1px solid #E5E7EB",
                              borderRadius: 2,
                              px: 1.5,
                              py: 1.1,
                              display: "grid",
                              gridTemplateColumns: { xs: "1fr auto", md: "1fr auto auto" },
                              gap: 1,
                              alignItems: "center",
                              background: "white",
                            }}
                          >
                            <Typography sx={{ fontSize: 13, color: "#111827", fontWeight: 700 }}>
                              {owner.name}
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: "#374151", fontWeight: 700 }}>
                              {owner.percentage}%
                            </Typography>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => removeSubOwner(owner.userId)}
                              startIcon={<DeleteOutlineIcon fontSize="small" />}
                              sx={{ textTransform: "none", minWidth: 48, color: "#DC2626" }}
                            >
                              Remove
                            </Button>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )}

            {!stepOneHalfOpen && !step1Valid && (
              <Typography
                sx={{ mt: 2, color: "#B91C1C", fontWeight: 800, fontSize: 12 }}
              >
                Tree name, Entity, and Language are required.
              </Typography>
            )}
          </Box>
        )}

        {activeStep === 1 && (
          <Box sx={{ ...innerPanelSx, borderRadius: 4 }}>
            <Box
              sx={{
                mb: 1.5,
                border: "1px solid #E5E7EB",
                borderRadius: 2,
                px: 1.5,
                py: 1.2,
                background: "#F9FAFB",
                display: "flex",
                alignItems: { xs: "flex-start", md: "center" },
                justifyContent: "space-between",
                gap: 1.2,
                flexDirection: { xs: "column", md: "row" },
              }}
            >
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                <Chip
                  size="small"
                  label={`Editing group: ${currentRowGroup || "—"}`}
                  sx={{ fontWeight: 700, background: "#EEF2F7", color: "#111827" }}
                />
                <Chip
                  size="small"
                  label={`Editing server: ${currentRowServer || "—"}`}
                  sx={{ fontWeight: 700, background: "#EEF2F7", color: "#111827" }}
                />
              </Box>
              <Button
                size="small"
                variant="contained"
                startIcon={<SearchIcon fontSize="small" />}
                onClick={() => {
                  setGroupSearch(currentRowGroup);
                  setServerFilter(currentRowServer);
                  setGroupPage(1);
                }}
                disabled={!currentRowGroup && !currentRowServer}
                sx={addBtnSx(Boolean(currentRowGroup || currentRowServer))}
              >
                Use current row
              </Button>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "220px 1fr 110px" },
                gap: 1.5,
                alignItems: "center",
                mb: 2,
              }}
            >
              <TextField
                size="small"
                placeholder="Search by user group name"
                value={groupSearch}
                onChange={(e) => {
                  setGroupSearch(e.target.value);
                  setGroupPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>
                      <SearchIcon fontSize="small" />
                    </Box>
                  ),
                }}
              />

              <FormControl size="small" fullWidth>
                <InputLabel>Server</InputLabel>
                <Select
                  label="Server"
                  value={serverFilter}
                  onChange={(e) => {
                    setServerFilter(e.target.value);
                    setGroupPage(1);
                  }}
                >
                  <MenuItem value="">Select an option</MenuItem>
                  {serverOptions.map((server) => (
                    <MenuItem key={server} value={server}>
                      {server}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                sx={addBtnSx(true)}
                onClick={() => setGroupPage(1)}
              >
                Search
              </Button>
            </Box>

            {tradingGroupsError ? (
              <Typography sx={{ mb: 1.5, color: "#B91C1C", fontWeight: 800, fontSize: 12 }}>
                {tradingGroupsError}
              </Typography>
            ) : null}

            <TableContainer
              sx={{
                borderRadius: 3,
                overflowX: "auto",
                border: "1px solid #EEF2F7",
              }}
            >
              <Table
                size="small"
                sx={{
                  minWidth: groupTableMinWidth,
                  tableLayout: "fixed",
                  "& .MuiTableCell-root": {
                    fontFamily: '"Plus Jakarta Sans", sans-serif',
                  },
                }}
              >
                <colgroup>
                  {GROUP_TABLE_COLUMNS.map((column) => (
                    <col
                      key={column.field}
                      style={{
                        width: `${groupColumnWidths[column.field] || column.width}px`,
                      }}
                    />
                  ))}
                </colgroup>
                <TableHead>
                  <TableRow sx={{ background: "#F9F9F9" }}>
                    {GROUP_TABLE_COLUMNS.map((column) => (
                      <TableCell
                        key={column.field}
                        align={column.align || "left"}
                        sx={{
                          ...groupColumnCellSx[column.field],
                          fontSize: TABLE_HEADER_FONT_SIZE,
                          fontWeight: 800,
                          color: "#374151",
                          py: 1.6,
                          position: "relative",
                          pr: column.resizable === false ? 2 : "28px",
                          userSelect: "none",
                        }}
                      >
                        <Box
                          component="span"
                          title={column.label}
                          sx={{
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {column.label}
                        </Box>
                        {column.resizable !== false && (
                          <Box
                            component="span"
                            role="separator"
                            aria-label={`Resize ${column.label} column`}
                            aria-orientation="vertical"
                            aria-valuemin={column.minWidth}
                            aria-valuenow={groupColumnWidths[column.field] || column.width}
                            tabIndex={0}
                            onPointerDown={(event) =>
                              handleGroupColumnResizeStart(event, column)
                            }
                            onKeyDown={(event) =>
                              handleGroupColumnResizeKeyDown(event, column)
                            }
                            sx={{
                              position: "absolute",
                              top: 0,
                              right: 0,
                              width: "12px",
                              height: "100%",
                              cursor: "col-resize",
                              touchAction: "none",
                              "&::after": {
                                content: '""',
                                position: "absolute",
                                top: "22%",
                                bottom: "22%",
                                left: "50%",
                                width: "2px",
                                transform: "translateX(-50%)",
                                borderRadius: "2px",
                                backgroundColor: "rgba(21, 32, 42, 0.22)",
                              },
                              "&:hover::after, &:focus-visible::after": {
                                backgroundColor: "#15202A",
                              },
                              "&:focus-visible": {
                                outline: "2px solid #15202A",
                                outlineOffset: "-2px",
                              },
                            }}
                          />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tradingGroupsLoading ? (
                    <TableRow>
                      <TableCell colSpan={GROUP_TABLE_COLUMNS.length}>
                        <Box sx={{ py: 2, textAlign: "center", color: "#6B7280", fontSize: 12 }}>
                          Loading trading groups...
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : null}

                  {!tradingGroupsLoading && paginatedGroups.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={GROUP_TABLE_COLUMNS.length}>
                        <Box sx={{ py: 2, textAlign: "center", color: "#6B7280", fontSize: 12 }}>
                          No trading groups found.
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : null}

                  {paginatedGroups.map((group) => {
                    const checked = selectedGroupIds.includes(group.id);
                    return (
                      <TableRow
                        key={group.id}
                        hover
                        onClick={() => toggleGroup(group.id)}
                        sx={{ cursor: "pointer", background: checked ? "#EEF6FF" : "white" }}
                      >
                        <TableCell sx={{ ...groupColumnCellSx.select, textAlign: "center" }}>
                          <Checkbox
                            checked={checked}
                            onChange={() => toggleGroup(group.id)}
                            onClick={(event) => event.stopPropagation()}
                            sx={greenCheckboxSx}
                          />
                        </TableCell>
                        <TableCell
                          title={group.group}
                          sx={{ ...groupColumnCellSx.group, fontSize: 12, color: "#111827" }}
                        >
                          {group.group}
                        </TableCell>
                        <TableCell
                          title={group.server}
                          sx={{ ...groupColumnCellSx.server, fontSize: 12, color: "#111827" }}
                        >
                          {group.server}
                        </TableCell>
                        <TableCell
                          title={group.currency}
                          sx={{ ...groupColumnCellSx.currency, fontSize: 12, color: "#111827" }}
                        >
                          {group.currency}
                        </TableCell>
                        <TableCell
                          title={group.leverage}
                          sx={{ ...groupColumnCellSx.leverage, fontSize: 12, color: "#111827" }}
                        >
                          {group.leverage}
                        </TableCell>
                        <TableCell
                          title={group.mcso}
                          sx={{ ...groupColumnCellSx.mcso, fontSize: 12, color: "#111827" }}
                        >
                          {group.mcso}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                mt: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 1.2,
                flexWrap: "wrap",
              }}
            >
              <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                Total:&nbsp;<b style={{ color: "#111827" }}>{filteredGroups.length}</b>
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                  Pages:&nbsp;<b style={{ color: "#111827" }}>{groupPageCount}</b>
                </Typography>

                <Typography sx={{ fontSize: 13, color: "#6B7280" }}>Rows:</Typography>

                <Select
                  size="small"
                  value={groupRowsPerPage}
                  onChange={(event) => {
                    setGroupRowsPerPage(Number(event.target.value));
                    setGroupPage(1);
                  }}
                  sx={{
                    minWidth: 74,
                    height: 32,
                    borderRadius: 1.5,
                    "& .MuiSelect-select": {
                      py: "5px",
                    },
                  }}
                >
                  {GROUP_ROWS_OPTIONS.map((rowsOption) => (
                    <MenuItem key={rowsOption} value={rowsOption}>
                      {rowsOption}
                    </MenuItem>
                  ))}
                </Select>

                <Button
                  variant="outlined"
                  onClick={() => setGroupPage((page) => Math.max(1, page - 1))}
                  disabled={groupPage <= 1}
                  sx={groupArrowBtnSx(groupPage <= 1)}
                >
                  <ArrowBackIosNewSmallIcon sx={{ fontSize: 14 }} />
                </Button>

                {groupPageItems.map((pageItem, index) => {
                  const isNumber = typeof pageItem === "number";
                  const isCurrent = isNumber && pageItem === groupPage;

                  return (
                    <Button
                      key={`${pageItem}-${index}`}
                      onClick={() => isNumber && setGroupPage(pageItem)}
                      disabled={!isNumber}
                      sx={{
                        minWidth: 32,
                        height: 32,
                        borderRadius: 1.5,
                        px: 1.1,
                        color: isCurrent ? "#111827" : "#6B7280",
                        background: isCurrent ? "#EEF2F7" : "transparent",
                        fontWeight: isCurrent ? 800 : 600,
                        "&:hover": { background: isCurrent ? "#E5E7EB" : "#F3F4F6" },
                      }}
                    >
                      {pageItem}
                    </Button>
                  );
                })}

                <Button
                  variant="outlined"
                  onClick={() =>
                    setGroupPage((page) => Math.min(groupPageCount, page + 1))
                  }
                  disabled={groupPage >= groupPageCount}
                  sx={groupArrowBtnSx(groupPage >= groupPageCount)}
                >
                  <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
                </Button>
              </Box>

              {!step2Valid && (
                <Typography sx={{ color: "#B91C1C", fontWeight: 800, fontSize: 12 }}>
                  Select one group to continue.
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {activeStep === 2 && (
          <>
            <Typography sx={{ fontSize: 12, color: "#6B7280", mt: 0.5, mb: 2 }}>
              Distribute rebates and define allocation percentages.
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <Chip
                label={`Remaining share percentage: ${remainingShares}%`}
                sx={{
                  px: 1.2,
                  py: 2.2,
                  borderRadius: 999,
                  background: "#EEF2F7",
                  color: "#111827",
                  fontWeight: 800,
                  border: overflow ? "1px solid #B91C1C" : "1px solid transparent",
                }}
              />
            </Box>

            <Box sx={{ ...innerPanelSx, borderRadius: 4 }}>
              {form.splits.map((row, idx) => (
                <Box
                  key={`${row.ibId || "new"}-${idx}`}
                  sx={{
                    border: "1px solid #E5E7EB",
                    borderRadius: 3,
                    p: 2,
                    mb: 2,
                    background: "white",
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "2fr 180px" },
                      gap: 2,
                      mb: 1.5,
                    }}
                  >
                    <Autocomplete
                      size="small"
                      options={ibOptions}
                      loading={brokersLoading}
                      value={
                        ibOptions.find(
                          (ib) => String(ib.id) === String(row.ibId)
                        ) ||
                        (String(row.ibId || row.ibName).trim()
                          ? {
                              id: row.ibId || row.ibName,
                              name: row.ibName || `IB ${row.ibId}`,
                            }
                          : null)
                      }
                      onChange={(_, selectedOption) => {
                        setSplitField(idx, "ibId", selectedOption?.id ? String(selectedOption.id) : "");
                        setSplitField(idx, "ibName", selectedOption?.name ?? "");
                      }}
                      isOptionEqualToValue={(option, value) =>
                        String(option?.id ?? "") === String(value?.id ?? "")
                      }
                      getOptionLabel={(option) => option?.name ?? ""}
                      noOptionsText={
                        brokersError || "No IBs available."
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="List of IBs"
                          placeholder="Search IB..."
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <SearchIcon fontSize="small" />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                      )}
                    />

                    <TextField
                      size="small"
                      label="Shares %"
                      value={`${clampPercent(row.sharesPercent)}%`}
                      onChange={(e) =>
                        setSplitField(idx, "sharesPercent", clampPercent(e.target.value))
                      }
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                      gap: 1.5,
                      alignItems: "center",
                    }}
                  >
                    {[
                      { key: "showLink", label: "Show link" },
                      { key: "seeClients", label: "See clients" },
                      { key: "seeFullPercent", label: "See full %" },
                    ].map((p) => {
                      const checked = !!row[p.key];
                      return (
                        <Box
                          key={p.key}
                          onClick={() => setSplitField(idx, p.key, !checked)}
                          sx={{
                            cursor: "pointer",
                            border: "1px solid #E5E7EB",
                            borderRadius: 2,
                            px: 1.5,
                            py: 1.1,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            background: checked ? "#EEF6FF" : "white",
                          }}
                        >
                          <Checkbox
                            checked={checked}
                            onChange={() => setSplitField(idx, p.key, !checked)}
                            onClick={(e) => e.stopPropagation()}
                            sx={greenCheckboxSx}
                          />
                          <Typography
                            sx={{ fontSize: 12, color: "#374151", fontWeight: 700 }}
                          >
                            {p.label}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      mt: 1.5,
                    }}
                  >
                    {form.splits.length > 1 && (
                      <Button
                        onClick={() => removeSplitRow(idx)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 800,
                          color: "#B91C1C",
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </Box>
                </Box>
              ))}

              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Button variant="contained" onClick={addSplitRow} sx={addBtnSx(true)}>
                  + Add split
                </Button>

                <Typography
                  sx={{
                    fontWeight: 800,
                    color: overflow ? "#B91C1C" : "#6B7280",
                    fontSize: 12,
                  }}
                >
                  Total: {totalShares}% {overflow ? "(exceeds 100%)" : ""}
                </Typography>

                {!overflow && totalShares !== 100 && (
                  <Typography sx={{ fontWeight: 800, color: "#B45309", fontSize: 12 }}>
                    Must equal 100% to save
                  </Typography>
                )}

                {form.splits.some((s) => !String(s.ibId).trim()) && (
                  <Typography sx={{ fontWeight: 800, color: "#B91C1C", fontSize: 12 }}>
                    Every split must have a valid IB ID.
                  </Typography>
                )}
              </Box>
            </Box>
          </>
        )}

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.2 }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={saving || loadingTree}
            sx={blackBtnSx}
          >
            Back
          </Button>

          {activeStep < 2 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!canContinue || saving || loadingTree}
              sx={addBtnSx(canContinue && !saving && !loadingTree)}
            >
              Continue
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || loadingTree || !form.id}
              sx={addBtnSx(!saving && !loadingTree && !!form.id)}
            >
              {saving ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: "white" }} />
                  Saving...
                </Box>
              ) : (
                "Save changes"
              )}
            </Button>
          )}
        </Box>
      </Box>

      {/* SUCCESS MODAL */}
      <Dialog
        open={openSuccessModal}
        onClose={() => setOpenSuccessModal(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 0,
            minWidth: { xs: "calc(100% - 32px)", sm: 460 },
          },
        }}
      >
        <DialogContent sx={{ p: 3, textAlign: "center" }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
            <CheckCircleIcon sx={{ fontSize: 54, color: "#16A34A" }} />
          </Box>

          <Typography sx={{ fontSize: 22, fontWeight: 900, color: "#111827" }}>
            Tree updated successfully
          </Typography>

          <Typography sx={{ mt: 1, fontSize: 14, color: "#6B7280" }}>
            The broker tree has been updated successfully and the changes are now available in the system.
          </Typography>

          {updatedTreeId ? (
            <Typography sx={{ mt: 2, fontSize: 14, fontWeight: 800, color: "#111827" }}>
              Tree ID: #{updatedTreeId}
            </Typography>
          ) : null}

          <Box sx={{ mt: 3, display: "flex", gap: 1.2, justifyContent: "center" }}>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                setOpenSuccessModal(false);
                navigate(-1);
              }}
              sx={{
                textTransform: "none",
                fontWeight: 900,
                borderRadius: 2,
              }}
            >
              Back to Trees
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}